import { useQuery } from '@tanstack/react-query';
import {
  currencyForTimeZone,
  DEFAULT_PRICES,
  formatPrice,
  isCurrency,
  planPrice,
  type Currency,
  type PlanId,
  type PriceTable,
} from '@bannermatic/core';
import { create } from 'zustand';
import { api } from './api';
import { localeTag, useLocale } from './i18n';

export interface PricingInfo {
  base: Currency;
  currencies: Currency[];
  prices: PriceTable;
  detected: Currency | null;
  chargeCurrency: Currency;
}

const FALLBACK: PricingInfo = {
  base: 'USD',
  currencies: ['USD'],
  prices: { USD: DEFAULT_PRICES.USD },
  detected: null,
  chargeCurrency: 'USD',
};

function savedCurrency(): Currency | null {
  try {
    const v = localStorage.getItem('bm.currency');
    return isCurrency(v) ? v : null;
  } catch {
    return null;
  }
}

/** The visitor's explicit choice; null means "detect". */
export const useCurrencyChoice = create<{ chosen: Currency | null; choose(c: Currency): void }>(
  (set) => ({
    chosen: savedCurrency(),
    choose(c) {
      try {
        localStorage.setItem('bm.currency', c);
      } catch {
        // private mode: the choice lasts for this page only
      }
      set({ chosen: c });
    },
  }),
);

function guessFromTimeZone(): Currency | null {
  try {
    return currencyForTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}

/**
 * Plan prices in the visitor's currency: their own choice, else the country from the CDN,
 * else a guess from the browser time zone, else USD.
 */
export function usePricing() {
  const { data } = useQuery({
    queryKey: ['pricing'],
    queryFn: () => api<PricingInfo>('/api/pricing', { workspace: false }),
    staleTime: 5 * 60_000,
  });
  const info = data ?? FALLBACK;
  const chosen = useCurrencyChoice((s) => s.chosen);
  const choose = useCurrencyChoice((s) => s.choose);
  const locale = useLocale((s) => s.locale);
  const offered = (c: Currency | null): c is Currency => !!c && info.currencies.includes(c);
  const guess = guessFromTimeZone();
  const currency: Currency = offered(info.detected)
    ? info.detected
    : offered(guess)
      ? guess
      : offered(chosen)
        ? chosen
        : info.base;

  const price = (plan: PlanId, interval: 'month' | 'year', c: Currency = currency) =>
    planPrice(info.prices, plan, interval, c);
  const format = (amount: number, c: Currency = currency) =>
    formatPrice(amount, c, localeTag(locale));

  return {
    ...info,
    currency,
    setCurrency: choose,
    price,
    format,
    /** Price per month for the interval (yearly prices are shown per month). */
    monthly(plan: PlanId, interval: 'month' | 'year', c: Currency = currency) {
      const p = price(plan, interval, c);
      return { ...p, amount: interval === 'year' ? Math.round(p.amount / 1200) * 100 : p.amount };
    },
    /** Yearly saving in percent (by the Pro plan in the shown currency). */
    yearlyDiscount(c: Currency = currency) {
      const m = price('pro', 'month', c).amount;
      const y = price('pro', 'year', c).amount;
      return m > 0 ? Math.max(0, Math.round((1 - y / (12 * m)) * 100)) : 0;
    },
    /** True when the card is charged in another currency than the one shown. */
    convertedAtCheckout: currency !== info.chargeCurrency,
  };
}
